import { tryOnScopeDispose, useTimeoutFn } from '@vueuse/core'
import { ref, shallowRef, type Ref, type ShallowRef } from 'vue'
import { emitNetworkDegraded, emitNetworkRecovered } from '@/services/network/network_events'
import { SmartFetchError } from '@/services/network/smart_fetch'

// useApiRequest — единый Vue-композабл для любого API-вызова.
// Reactive state (data / error / loading) + автоматическое управление
// AbortController:
//   1. Новый execute() отменяет предыдущий — нет race-conditions при
//      быстром перепечатывании поиска / щёлкании фильтров.
//   2. effectScope dispose (unmount компонента / destroy store) →
//      автоотмена текущего in-flight запроса.
//   3. Отмена не считается ошибкой — error.value остаётся как был,
//      execute() возвращает null без throw.
//   4. Опция `debounce` — execute копит args в течение Nms тишины, потом
//      запускает один реальный fetch. Каждый новый execute сбрасывает
//      таймер (useTimeoutFn). Pending in-flight fetch abort'ится при новом execute.
//
// fn получает AbortSignal первым параметром — обязательно пробрасывай его
// в apiClient.GET/POST/PATCH/DELETE (или fetch / любой другой async вызов).
//
// Пример (без debounce — mutation):
//   const update = useApiRequest((signal, patch: { name: string }) =>
//     apiClient.PATCH('/users/me', { body: { user: patch }, signal }))
//   update.execute({ name: 'alice' })
//
// Пример (с debounce — поиск):
//   const search = useApiRequest(
//     (signal, q: string) => apiClient.GET('/users', { params: { query: { q } }, signal }),
//     { debounce: 500 },
//   )
//   watch(query, () => search.execute(query.value))   // typing → один fetch
//   search.cancel()   // алиас для abort()

export interface UseApiRequestOptions {
  /**
   * Задержка в мс между последним execute() и реальным fetch'ем.
   * Каждый новый execute в течение окна сбрасывает таймер.
   * In-flight fetch (если уже стартанул) abort'ится при новом execute.
   *
   * В debounced-режиме execute() НЕ throws при ошибках — ошибки попадают
   * только в error.value. Watch'ите его реактивно.
   */
  debounce?: number
}

export interface UseApiRequest<TArgs extends unknown[], TData> {
  data: ShallowRef<TData | null>
  error: ShallowRef<unknown>
  loading: Ref<boolean>
  execute: (...args: TArgs) => Promise<TData | null>
  abort: () => void
  cancel: () => void
}

export function useApiRequest<TArgs extends unknown[], TData>(
  fn: (signal: AbortSignal, ...args: TArgs) => Promise<TData>,
  opts: UseApiRequestOptions = {},
): UseApiRequest<TArgs, TData> {
  const data = shallowRef<TData | null>(null)
  const error = shallowRef<unknown>(null)
  const loading = ref(false)
  let currentCtrl: AbortController | null = null
  let pendingArgs: TArgs | null = null
  let pendingResolve: ((v: TData | null) => void) | null = null

  const { start: scheduleRun, stop: stopPending } = useTimeoutFn(
    () => {
      const myResolve = pendingResolve
      const myArgs = pendingArgs
      pendingResolve = null
      pendingArgs = null
      if (!myArgs) return
      run(...myArgs).then(
        (v) => myResolve?.(v),
        // error уже в error.value, наружу не throw'ем
        () => myResolve?.(null),
      )
    },
    opts.debounce ?? 0,
    { immediate: false },
  )

  function abort(): void {
    stopPending()
    pendingResolve?.(null)
    pendingResolve = null
    pendingArgs = null
    currentCtrl?.abort()
    currentCtrl = null
    loading.value = false
  }

  async function run(...args: TArgs): Promise<TData | null> {
    currentCtrl?.abort()
    const ctrl = new AbortController()
    currentCtrl = ctrl
    loading.value = true
    error.value = null
    try {
      const result = await fn(ctrl.signal, ...args)
      if (ctrl.signal.aborted) return null
      data.value = result
      emitNetworkRecovered()
      return result
    } catch (e) {
      if (isAbort(e, ctrl.signal)) return null
      reportDegradation(e)
      error.value = e
      throw e
    } finally {
      if (currentCtrl === ctrl) {
        currentCtrl = null
        loading.value = false
      }
    }
  }

  function execute(...args: TArgs): Promise<TData | null> {
    if (!opts.debounce) return run(...args)

    // Debounced mode: каждый execute reset'ит таймер. In-flight fetch
    // (если уже стартанул) abort'ится. Предыдущий ожидающий промис
    // резолвится как cancelled (null) — caller получает null, fetch не
    // случился.
    stopPending()
    currentCtrl?.abort()
    loading.value = true
    error.value = null

    return new Promise<TData | null>((resolve) => {
      pendingResolve?.(null)
      pendingResolve = resolve
      pendingArgs = args
      scheduleRun()
    })
  }

  tryOnScopeDispose(abort)

  return { data, error, loading, execute, abort, cancel: abort }
}

function isAbort(e: unknown, ourSignal: AbortSignal): boolean {
  if (ourSignal.aborted) return true
  if (e instanceof DOMException && e.name === 'AbortError') return true
  if (e instanceof SmartFetchError && e.reason === 'user') return true
  return false
}

// Сетевая деградация (ttfb/overall/network) при формально online-браузере —
// сигналим глобально (залипший тост). Любой успешный ответ (emitNetworkRecovered
// в run) снимает тост — сервер снова отвечает. Офлайн (navigator.onLine === false)
// НЕ трогаем: его покрывает OfflineBanner, дублировать тостом не нужно. `user`-отмена
// сюда не доходит — отсекается в isAbort выше.
function reportDegradation(e: unknown): void {
  if (!(e instanceof SmartFetchError)) return
  if (e.reason === 'user') return
  if (navigator.onLine === false) return
  emitNetworkDegraded(e.reason)
}
